import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, WHATSAPP_NUMBER } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ShoppingBag, MessageCircle, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((res) => {
        setProduct(res.data);
        setActiveImage(res.data.image_url);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#5C5C5C]">
        Cargando pieza...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <p className="font-serif-editorial text-3xl mb-4">Pieza no encontrada</p>
        <Link to="/catalogo" className="btn-secondary">Volver a la colección</Link>
      </div>
    );
  }

  const handleAdd = () => {
    addItem(product, 1);
    toast.success(`${product.name} añadido a la cesta`);
  };

  const directWa = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Hola GALOLÀ! Me interesa "${product.name}" (€${product.price.toFixed(2)}). ¿Está disponible?`
  )}`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="product-detail-page">
      <Link to="/catalogo" className="label-tracked text-[#5C5C5C] hover:text-[#C27A62] inline-flex items-center gap-1 mb-8">
        <ChevronLeft size={14} /> Volver a colección
      </Link>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        <div className="space-y-4">
          <div className="aspect-[3/4] bg-[#EDE8DD] overflow-hidden">
            <img
              src={activeImage || product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
          {(product.extra_images && product.extra_images.length > 0) && (
            <div className="grid grid-cols-4 gap-3">
              {[product.image_url, ...product.extra_images].map((src, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setActiveImage(src)}
                  className={`aspect-[3/4] overflow-hidden bg-[#EDE8DD] border transition-colors ${
                    (activeImage || product.image_url) === src
                      ? "border-[#C27A62]"
                      : "border-transparent hover:border-[#E5E0D8]"
                  }`}
                  data-testid={`thumb-${idx}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <div className="label-tracked text-[#C27A62] mb-3">{product.category}</div>
          <h1 className="font-serif-editorial text-4xl md:text-5xl leading-tight text-[#1E1E1E]">
            {product.name}
          </h1>
          <div className="text-2xl font-serif-editorial text-[#1E1E1E] mt-4" data-testid="product-price">
            €{product.price.toFixed(2)}
          </div>

          <div className="my-8 divider-thin" />

          <p className="text-[#5C5C5C] leading-relaxed whitespace-pre-line">
            {product.description}
          </p>

          {product.materials && (
            <div className="mt-6">
              <div className="label-tracked text-[#1E1E1E] mb-2">Materiales</div>
              <p className="text-[#5C5C5C] text-sm">{product.materials}</p>
            </div>
          )}

          {product.measurements && (
            <div className="mt-6">
              <div className="label-tracked text-[#1E1E1E] mb-2">Medidas</div>
              <p className="text-[#5C5C5C] text-sm">{product.measurements}</p>
            </div>
          )}

          <div className="mt-10 flex flex-col gap-3">
            <button
              onClick={handleAdd}
              className="btn-primary"
              disabled={!product.in_stock}
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag size={16} />
              {product.in_stock ? "Añadir a la cesta" : "Sin stock"}
            </button>
            <a
              href={directWa}
              target="_blank"
              rel="noreferrer"
              className="btn-whatsapp"
              data-testid="ask-whatsapp-btn"
            >
              <MessageCircle size={18} />
              Preguntar por WhatsApp
            </a>
          </div>

          <div className="mt-10 text-xs text-[#5C5C5C] leading-relaxed border-t border-[#E5E0D8] pt-6">
            <p className="mb-1">Pieza única hecha a mano. Pequeñas variaciones forman parte de su carácter.</p>
            <p>Envío peninsular en 3-5 días laborables. Internacional consultar.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
